import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import LogoDark from '../../images/logo/logo-dark.svg';
import Logo from '../../images/logo/logo.svg';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import SvgIcon from '../../components/SvgIcon/SvgIcon';

interface LoginForm {
  email: string;
  password: string;
}

const initialLoginForm: LoginForm = {
  email: '',
  password: '',
};

const SignIn: React.FC = () => {
  const { handleLogin, errorMessage, showError } = useAuth();
  const auth = useSelector((state: RootState) => state.auth);
  const [loginForm, setLoginForm] = useState<LoginForm>(initialLoginForm);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (auth.isBlocked) {
      setFormErrors((prev) => ({
        ...prev,
        general: 'Tu cuenta ha sido bloqueada por múltiples intentos fallidos.',
      }));
    }
  }, [auth.isBlocked]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};
    if (!loginForm.email)
      errors.email = 'El correo electrónico es obligatorio.';
    if (!loginForm.password) errors.password = 'La contraseña es obligatoria.';
    return errors;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    if (auth.isBlocked) {
      showError({ error: 'Tu cuenta está bloqueada por seguridad.' });
      setIsSubmitting(false);
      return;
    }

    try {
      await handleLogin({
        email: loginForm.email,
        password: loginForm.password,
      });
      setLoginForm(initialLoginForm);
      setFormErrors({});
    } catch (error) {
      setFormErrors((prev) => ({
        ...prev,
        general: errorMessage.error || 'Error al iniciar sesión.',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const generalError = formErrors.general || errorMessage.error;

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-white p-4 dark:bg-boxdark-2 dark:text-bodydark">

      {/* 🎨 FONDO SVG DE BURBUJAS DE COLOR (incluye los puntitos) */}
      <div className="pointer-events-none absolute inset-0 z-0 dark:opacity-40">
        <SvgIcon name="background-bubbles" width="100%" height="100%" />
      </div>

      <div className="relative z-10 grid w-full max-w-6xl grid-cols-1 items-center gap-10 xl:grid-cols-2">

        {/* 🔹 Sección izquierda */}
        <div className="hidden flex-col items-center gap-8 text-center xl:flex">
          <Link className="inline-block" to="/">
            <img className="block h-auto w-56 dark:hidden" src={LogoDark} alt="Codenova" />
            <img className="hidden h-auto w-56 dark:block" src={Logo} alt="Codenova" />
          </Link>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-black dark:text-white">
              Bienvenido a Codenova
            </h1>
            <p className="mx-auto max-w-sm text-graydark dark:text-bodydark1">
              Tu plataforma inteligente para ventas, inventario y facturación
              electrónica.
            </p>
          </div>

          <div className="relative mt-4 pb-6">
            <SvgIcon name="dashboard-preview" height={220} width={320} />

            <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-2xl border border-stroke bg-white px-4 py-3 shadow-default dark:border-strokedark dark:bg-boxdark">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                <ShieldCheck size={16} />
              </span>
              <span className="text-sm font-semibold text-black dark:text-white">
                Seguro. Confiable. Escalable.
              </span>
            </div>
          </div>
        </div>

        {/* 🔹 Sección derecha (formulario) */}
        <div className="mx-auto w-full max-w-md rounded-3xl border border-stroke bg-white p-8 shadow-[0_20px_60px_rgba(60,80,224,0.15)] dark:border-strokedark dark:bg-boxdark sm:p-10">

          {/* Logo (solo visible antes del breakpoint xl) */}
          <Link to="/" className="mb-6 flex justify-center xl:hidden">
            <img className="block h-auto w-40 dark:hidden" src={LogoDark} alt="Codenova" />
            <img className="hidden h-auto w-40 dark:block" src={Logo} alt="Codenova" />
          </Link>

          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
              Bienvenido a Codenova
            </h2>
            <p className="mt-2 text-sm text-body dark:text-bodydark">
              Inicia sesión para continuar
            </p>
          </div>

          {generalError && (
            <div className="mb-6 flex items-center gap-2 rounded-lg bg-danger/10 p-3 text-sm text-danger">
              <AlertCircle size={18} className="shrink-0" />
              <span>{generalError}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            {/* 📧 Email */}
            <div className="mb-4">
              <label className="mb-2.5 block font-medium text-black dark:text-white">
                Correo electrónico
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                  <SvgIcon name="email" height={20} width={20} />
                </span>
                <input
                  type="email"
                  name="email"
                  placeholder="Ingrese su correo"
                  value={loginForm.email}
                  onChange={onInputChange}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3.5 pl-12 pr-4 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                />
              </div>
              {formErrors.email && (
                <p className="mt-1 text-sm text-danger">{formErrors.email}</p>
              )}
            </div>

            {/* 🔒 Password */}
            <div className="mb-6">
              <label className="mb-2.5 block font-medium text-black dark:text-white">
                Contraseña
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                  <SvgIcon name="lock-filled" height={20} width={20} />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Ingrese su contraseña"
                  value={loginForm.password}
                  onChange={onInputChange}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3.5 pl-12 pr-11 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-body hover:text-primary dark:text-bodydark"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {formErrors.password && (
                <p className="mt-1 text-sm text-danger">{formErrors.password}</p>
              )}
            </div>

            {/* 🚀 Botón */}
            <button
              type="submit"
              disabled={isSubmitting || auth.isBlocked}
              className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-body dark:text-bodydark">
            ¿No tienes cuenta?{' '}
            <span className="font-medium text-black dark:text-white">
              Contacta al administrador
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
